import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { 
  LayoutDashboard, 
  Calendar, 
  FileText, 
  Youtube, 
  ShoppingBag,
  Package,
  BarChart3,
  MessageSquare,
  Mic,
  LogOut,
  Skull,
  Menu,
  X
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const adminLinks = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/admin/events", label: "Events", icon: Calendar },
  { path: "/admin/shop", label: "Shop", icon: ShoppingBag },
  { path: "/admin/orders", label: "Orders", icon: Package },
  { path: "/admin/lightbox", label: "Lightbox", icon: MessageSquare },
  { path: "/admin/fan-questions", label: "Fan Questions", icon: Mic },
  { path: "/admin/content", label: "Content", icon: FileText },
  { path: "/admin/youtube", label: "YouTube", icon: Youtube },
];

export default function AdminLayout() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await api.getCurrentUser();
        setUser(userData);
      } catch {
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await api.logout();
      toast.success("Logged out successfully");
      navigate("/admin/login");
    } catch {
      toast.error("Logout failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-demolition-black flex items-center justify-center">
        <div className="animate-pulse">
          <Skull className="w-16 h-16 text-demolition-red" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-demolition-black flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-demolition-paper border-r border-white/10 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/admin" className="flex items-center gap-3">
              <Skull className="w-8 h-8 text-demolition-red" />
              <span className="font-russo text-lg tracking-tighter text-white uppercase">
                ADMIN
              </span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {adminLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`admin-nav-${link.label.toLowerCase()}`}
                  className={`flex items-center gap-3 px-4 py-3 font-teko text-lg tracking-wider uppercase transition-colors ${
                    isActive 
                      ? "bg-demolition-red text-white" 
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-white/10">
            <p className="text-xs text-white/50 font-mono mb-3 truncate">
              {user?.email}
            </p>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-3 px-4 py-3 w-full font-teko text-lg tracking-wider uppercase text-white/70 hover:bg-white/5 hover:text-demolition-red transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>

          {/* View Site Link */}
          <div className="p-4 border-t border-white/10">
            <Link
              to="/"
              target="_blank"
              className="flex items-center justify-center gap-2 px-4 py-2 border border-demolition-red text-demolition-red font-teko text-lg tracking-wider uppercase hover:bg-demolition-red hover:text-white transition-colors"
            >
              View Site
            </Link>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-demolition-paper border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white p-2"
            data-testid="admin-mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <span className="font-russo text-lg text-white uppercase">Admin</span>
          <div className="w-10" />
        </div>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
